import { FC, useMemo } from "react";

import { Grid } from "@mui/material";
import { MembershipValidityPeriod } from "@simplyk/common";
import { useFormContext, useWatch } from "react-hook-form";

import { useRateFieldName } from "../../hooks/useRateFieldName";

import { useTranslate } from "@/common/hooks/useTranslate";
import { DashboardRadioGroup } from "@/dashboard-design-system/RadioGroup";
import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";
import { StepTicketsFormData } from "@/features/LiveFormEditor/Steps/StepTickets/types";

export const ValidityPeriodField: FC = () => {
  const { t } = useTranslate();
  const { control, setValue, clearErrors } = useFormContext<StepTicketsFormData>();

  const validityField = useRateFieldName("membershipValidityPeriod");
  const atDateField = useRateFieldName("membershipValidityPeriodAtDate");
  const autoRenewalField = useRateFieldName("allowAutomaticRenewal");

  const currentValue = useWatch({ control, name: validityField });

  const topRadioValue: MembershipValidityPeriod = (() => {
    if (currentValue === MembershipValidityPeriod.AtDate) {
      return MembershipValidityPeriod.OneYearAfterPayment;
    }
    return currentValue ?? MembershipValidityPeriod.NoExpiration;
  })();

  const values = useMemo(
    () => ({
      [MembershipValidityPeriod.NoExpiration]: t("dashboard", "ticketing.validityPeriod.noExpiration"),
      [MembershipValidityPeriod.OneMonthAfterPayment]: t(
        "dashboard",
        "ticketing.validityPeriod.oneMonthAfterPayment.short"
      ),
      [MembershipValidityPeriod.OneYearAfterPayment]: t(
        "dashboard",
        "ticketing.validityPeriod.oneYearAfterPayment.short"
      ),
      [MembershipValidityPeriod.CustomDuration]: t("dashboard", "ticketing.validityPeriod.customDuration"),
    }),
    [t]
  );

  const handleChange = (value: string) => {
    const next = value as MembershipValidityPeriod;

    setValue(validityField, next, { shouldDirty: true });

    const allowsAutoRenewal = [
      MembershipValidityPeriod.AtDate,
      MembershipValidityPeriod.OneYearAfterPayment,
      MembershipValidityPeriod.OneMonthAfterPayment,
    ].includes(next);
    setValue(autoRenewalField, allowsAutoRenewal, { shouldDirty: true });

    if (next !== MembershipValidityPeriod.OneYearAfterPayment && next !== MembershipValidityPeriod.AtDate) {
      setValue(atDateField, undefined, { shouldDirty: true });
      clearErrors(atDateField);
    }
  };

  return (
    <Grid item xs={12}>
      <DashboardRadioGroup
        key={topRadioValue}
        defaultValue={topRadioValue}
        row
        values={values}
        onChange={handleChange}
        label={
          <DashboardTypography variant="body2" sx={(theme) => ({ color: theme.palette.text.neutral.moderate })}>
            {t("dashboard", "ticketing.validityPeriod")}
          </DashboardTypography>
        }
        tooltipTextValues={{
          [MembershipValidityPeriod.OneMonthAfterPayment]: t(
            "dashboard",
            "ticketing.validityPeriod.monthlyHelp"
          ) as string,
        }}
      />
    </Grid>
  );
};
